/* eslint-disable react/prop-types */
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../../api/axios";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

export default function PayrollRun() {
  const navigate = useNavigate();

  const [month, setMonth] = useState(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${d.getFullYear()}-${m}`;
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [progressText, setProgressText] = useState("");
  const [activeRunId, setActiveRunId] = useState(null);

  const isMountedRef = useRef(true);
  const stopGenerationRef = useRef(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopGenerationRef.current = true;
    };
  }, []);

  const normalizeRows = (data) => {
    if (Array.isArray(data?.data)) return data.data; // Laravel paginate
    if (Array.isArray(data)) return data;
    return [];
  };

  const logAxios = (e) => {
    // eslint-disable-next-line no-console
    console.log("AXIOS DEBUG:", {
      baseURL: e?.config?.baseURL,
      url: e?.config?.url,
      fullURL: `${e?.config?.baseURL || ""}${e?.config?.url || ""}`,
      method: e?.config?.method,
      status: e?.response?.status,
      response: e?.response?.data,
    });
  };

  const load = useCallback(
    async (preferredMonth = month) => {
      setError("");
      setLoading(true);

      try {
        const res = await axios.get("/payroll-run", { params: { month: preferredMonth } });
        const nextRows = normalizeRows(res?.data);
        if (isMountedRef.current) {
          setRows(nextRows);
        }
        return nextRows;
      } catch (e) {
        logAxios(e);
        const msg = e?.response?.data?.message || e?.message || "Failed to load payroll runs.";
        if (isMountedRef.current) {
          setError(msg);
          setRows([]);
        }
        return [];
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [month]
  );

  const upsertLocalRunProgress = useCallback((payload) => {
    if (!payload?.run_id) return;

    setRows((prev) => {
      const idx = prev.findIndex((r) => Number(r.id) === Number(payload.run_id));

      const patch = {
        id: payload.run_id,
        month: payload.month,
        status: payload.status ?? "draft",
        generation_status: payload.generation_status,
        generation_processed: payload.generation_processed ?? 0,
        generation_total: payload.generation_total ?? 0,
      };

      if (idx === -1) {
        return [patch, ...prev];
      }

      const cloned = [...prev];
      cloned[idx] = { ...cloned[idx], ...patch };
      return cloned;
    });
  }, []);

  const openItems = useCallback(
    async (runId) => {
      navigate(`/payroll/history?run_id=${runId}`);
    },
    [navigate]
  );

  const pollSingleRunStatus = useCallback(async (runId) => {
    if (!runId) return null;

    try {
      const res = await axios.get(`/payroll-run/${runId}/status`);
      const payload = res?.data?.data ?? res?.data ?? {};

      if (payload?.id) {
        setRows((prev) =>
          prev.map((r) => (Number(r.id) === Number(runId) ? { ...r, ...payload } : r))
        );
      }

      return payload;
    } catch (e) {
      logAxios(e);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!activeRunId || !generating) return undefined;

    const timer = setInterval(() => {
      pollSingleRunStatus(activeRunId);
    }, 2000);

    return () => clearInterval(timer);
  }, [activeRunId, generating, pollSingleRunStatus]);

  const generatePayrollInBatches = useCallback(async () => {
    setError("");
    setGenerating(true);
    stopGenerationRef.current = false;

    let offset = 0;
    let lastPayload = null;

    try {
      setProgressText(`Starting payroll generation for ${month}...`);

      while (!stopGenerationRef.current) {
        const res = await axios.post("/payroll-run/generate", {
          month,
          offset,
          chunk_size: 500,
        });

        const payload = res?.data?.data ?? {};
        lastPayload = payload;

        if (!payload?.run_id) {
          throw new Error("Generation response is missing run information.");
        }

        setActiveRunId(payload.run_id);
        upsertLocalRunProgress(payload);

        const processed = Number(payload.generation_processed || 0);
        const total = Number(payload.generation_total || 0);

        if (isMountedRef.current) {
          setProgressText(
            payload.done
              ? `Payroll generation completed. ${processed}/${total} processed.`
              : `Generating payroll... ${processed}/${total} processed.`
          );
        }

        if (payload.done) {
          break;
        }

        if (!payload.processed_this_batch || Number(payload.processed_this_batch) <= 0) {
          throw new Error(
            "Generation stopped unexpectedly. No records were processed in the last batch."
          );
        }

        offset = Number(payload.next_offset ?? 0);

        if (offset <= 0) {
          throw new Error("Generation stopped unexpectedly. Invalid next batch offset.");
        }
      }

      if (stopGenerationRef.current) {
        return;
      }

      await load(month);

      if (isMountedRef.current) {
        window.alert("Payroll run generated successfully.");
      }
    } catch (e) {
      logAxios(e);
      const msg = e?.response?.data?.message || e?.message || "Failed to generate payroll run.";

      if (isMountedRef.current) {
        setError(msg);
        setProgressText(msg);
        window.alert(msg);
      }

      if (lastPayload?.run_id) {
        await pollSingleRunStatus(lastPayload.run_id);
      }
    } finally {
      if (isMountedRef.current) {
        setGenerating(false);
      }
    }
  }, [load, month, pollSingleRunStatus, upsertLocalRunProgress]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Month", accessor: "month" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Generation",
        accessor: "generation_status",
        Cell: ({ row }) => {
          const r = row?.original || {};
          const total = Number(r.generation_total || 0);
          const done = Number(r.generation_processed || 0);
          const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
          const showBar = ["processing", "done"].includes(r.generation_status) && total > 0;

          return (
            <MDBox>
              <MDTypography variant="button">
                {r.generation_status || "idle"} {total > 0 ? `(${done}/${total})` : ""}
              </MDTypography>

              {showBar && (
                <MDBox
                  mt={0.5}
                  sx={{
                    width: 180,
                    height: 8,
                    borderRadius: 4,
                    background: "#eee",
                    overflow: "hidden",
                  }}
                >
                  <MDBox
                    sx={{
                      width: `${pct}%`,
                      height: 8,
                      borderRadius: 4,
                      background: r.generation_status === "done" ? "#2e7d32" : "#4caf50",
                    }}
                  />
                </MDBox>
              )}

              {r.generation_status === "failed" && r.generation_error && (
                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                  {r.generation_error}
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      { Header: "Created At", accessor: "created_at" },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const r = row?.original || {};
          const runId = r.id;

          const isDone = r.generation_status === "done";
          const isFinal = r.status === "finalized";
          const isProcessing = r.generation_status === "processing";

          const goPreview = () =>
            navigate(`/payroll/preview?month=${encodeURIComponent(r.month || "")}`);

          const goExitDue = () =>
            navigate(`/payroll/exit-due-review?month=${encodeURIComponent(r.month || "")}`);

          const goTaxes = () => navigate(`/payroll/history?run_id=${runId}&tab=taxes`);
          const goItems = () => navigate(`/payroll/history?run_id=${runId}&tab=items`);

          const viewPayslip = async () => {
            if (!runId) return;
            const pfno = window.prompt("Enter PF number to view payslip for this run:");
            if (!pfno) return;
            navigate(`/payroll/payslip?run_id=${runId}&pfno=${encodeURIComponent(pfno.trim())}`);
          };

          const continueGenerate = async () => {
            if (!r?.month) return;

            setMonth(r.month);
            setError("");
            setGenerating(true);
            setActiveRunId(runId);
            stopGenerationRef.current = false;

            let offset = Number(r.generation_processed || 0);

            try {
              while (!stopGenerationRef.current) {
                const res = await axios.post("/payroll-run/generate", {
                  month: r.month,
                  offset,
                  chunk_size: 500,
                });

                const payload = res?.data?.data ?? {};
                upsertLocalRunProgress(payload);

                const processed = Number(payload.generation_processed || 0);
                const total = Number(payload.generation_total || 0);

                setProgressText(
                  payload.done
                    ? `Payroll generation completed. ${processed}/${total} processed.`
                    : `Generating payroll... ${processed}/${total} processed.`
                );

                if (payload.done) break;

                if (!payload.processed_this_batch || Number(payload.processed_this_batch) <= 0) {
                  throw new Error(
                    "Generation stopped unexpectedly. No records were processed in the last batch."
                  );
                }

                offset = Number(payload.next_offset ?? 0);

                if (offset <= 0) {
                  throw new Error("Generation stopped unexpectedly. Invalid next batch offset.");
                }
              }

              if (!stopGenerationRef.current) {
                await load(r.month);
                window.alert("Payroll run generated successfully.");
              }
            } catch (e) {
              logAxios(e);
              const msg =
                e?.response?.data?.message ||
                e?.message ||
                "Failed to continue payroll generation.";
              setError(msg);
              setProgressText(msg);
              window.alert(msg);
            } finally {
              setGenerating(false);
            }
          };

          const finalizeRun = async () => {
            if (!runId) return;
            if (!window.confirm("Finalize this payroll run? This should lock the month.")) return;

            try {
              const res = await axios.post(`/payroll-run/${runId}/finalize`);
              window.alert(res?.data?.message || "Finalized");
              await load(r.month || month);
            } catch (e) {
              logAxios(e);
              const data = e?.response?.data;
              const msg = data?.message || e?.message || "Failed to finalize payroll run.";
              window.alert(msg);

              if (data?.code === "EXIT_DUE_UNRESOLVED") {
                navigate(`/payroll/exit-due-review?month=${encodeURIComponent(r.month || "")}`);
              }

              if (data?.code === "PAYROLL_NOT_DONE") {
                openItems(runId);
              }
            }
          };

          return (
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton size="small" color="info" onClick={goPreview} disabled={!isDone}>
                Preview
              </MDButton>

              <MDButton size="small" color="dark" onClick={goItems} disabled={!isDone}>
                Items
              </MDButton>

              <MDButton size="small" color="secondary" onClick={goTaxes} disabled={!isDone}>
                Taxes
              </MDButton>

              <MDButton size="small" color="success" onClick={viewPayslip} disabled={!isDone}>
                Payslip
              </MDButton>

              <MDButton size="small" color="warning" onClick={goExitDue}>
                Exit Due
              </MDButton>

              <MDButton
                size="small"
                color="primary"
                onClick={continueGenerate}
                disabled={generating || isDone || !isProcessing}
              >
                Continue
              </MDButton>

              <MDButton
                size="small"
                color="error"
                onClick={finalizeRun}
                disabled={!isDone || isFinal}
              >
                {isFinal ? "Finalized" : "Finalize"}
              </MDButton>

              <MDButton
                size="small"
                color="light"
                onClick={() => openItems(runId)}
                disabled={!runId}
              >
                Open
              </MDButton>
            </MDBox>
          );
        },
      },
    ],
    [generating, load, month, navigate, openItems, pollSingleRunStatus, upsertLocalRunProgress]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox py={3}>
        <MDTypography variant="h5">Payroll Run</MDTypography>
        <MDTypography variant="button" color="text">
          Click “Generate Payroll Run” to process payroll in batches and show progress until
          completion.
        </MDTypography>

        {!!error && (
          <MDBox mt={2} p={2} borderRadius={2} sx={{ border: "1px solid #f5c2c7" }}>
            <MDTypography variant="button" color="error">
              {error}
            </MDTypography>
          </MDBox>
        )}

        {!!progressText && (
          <MDBox mt={2} p={2} borderRadius={2} sx={{ border: "1px solid #d1e7dd" }}>
            <MDTypography variant="button" color="success">
              {progressText}
            </MDTypography>
          </MDBox>
        )}

        <MDBox mt={3} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: "1px solid #ccc" }}
          />

          <MDButton color="success" onClick={generatePayrollInBatches} disabled={generating}>
            {generating ? "Generating..." : "Generate Payroll Run"}
          </MDButton>

          <MDButton color="info" onClick={() => load()} disabled={loading || generating}>
            {loading ? "Loading..." : "Refresh"}
          </MDButton>
        </MDBox>

        <MDBox mt={3}>
          <DataTable
            table={{ columns, rows }}
            entriesPerPage
            showTotalEntries
            noEndBorder
            canSearch
          />
        </MDBox>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}
